import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { Link } from 'react-router-dom';

// GraphQL Query
const GET_WORK_ORDERS = gql`
  query GetDashboardWorkOrders($input: WorkOrdersQueryInput) {
    workOrders(input: $input) {
      items {
        id
        isUrgent
        dueDate
      }
      total
    }
  }
`;

// Returns true when a work order should be counted on the dashboard, meaning
// it is actionable "today" - either no due date is set, or the due date has
// already arrived. This mirrors the default filter on the main Work Orders
// page (getWorkOrderFeed) so the dashboard count matches what a user actually
// sees when they click through.
const isDueTodayOrPast = (dueDateValue) => {
  if (!dueDateValue) return true;
  const dueDate = new Date(dueDateValue);
  if (Number.isNaN(dueDate.getTime())) return true;
  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);
  return dueDate.getTime() <= endOfToday.getTime();
};

const AREA_LABELS = {
  KIAWAH: 'Kiawah',
  ISLE_OF_PALMS: 'Isle of Palms',
  ISLE_OF_PALMS_WILD_DUNES: 'Isle of Palms - Wild Dunes',
  SEABROOK_ISLAND: 'Seabrook Island',
};

const WorkOrderWidget = ({ 
  user = null,
  isHousekeeper = false,
  isInspector = false,
  isLoggingOut = false,
  isCallCenter = false
}) => {
  // Determine if we should filter by assigned roles
  // HOUSEKEEPER_MANAGER should only see work orders assigned to their role
  const shouldFilterByRole = user?.role === 'HOUSEKEEPER_MANAGER';
  const userAreas = useMemo(() => (Array.isArray(user?.areas) ? user.areas : []), [user?.areas]);
  const assignedAreaLabel = useMemo(() => {
    if (userAreas.length === 0) {
      return 'Assigned area: All areas';
    }

    return `Assigned area${userAreas.length === 1 ? '' : 's'}: ${userAreas
      .map((area) => AREA_LABELS[area] || area.replace(/_/g, ' '))
      .join(', ')}`;
  }, [userAreas]);
  
  // Query for work orders - filter by user's assigned areas and role if applicable
  const { data: workOrdersData, loading: workOrdersLoading, error: workOrdersError } = useQuery(GET_WORK_ORDERS, {
    variables: {
      input: {
        status: 'OPEN',
        dateFrom: "2020-01-01",
        dateTo: "2030-12-31",
        page: 1,
        limit: 100,
        areas: user?.areas && user.areas.length > 0 ? user.areas : undefined,
        assignedRoles: shouldFilterByRole ? [user.role] : undefined,
        assignedStatus: 'UNASSIGNED'
      }
    },
    fetchPolicy: 'network-only', // Force a fresh fetch every time
    skip: !user || isLoggingOut // Skip query if user is not available or logging out
  });

  // Work orders stats.
  // We post-filter by due date on the client so the dashboard count matches
  // what the main Work Orders page shows by default (dueDate today or past).
  // Server already filters status=OPEN, assignedStatus=UNASSIGNED, role/area.
  const workOrders = useMemo(
    () => (workOrdersData?.workOrders?.items || []).filter((wo) => isDueTodayOrPast(wo.dueDate)),
    [workOrdersData?.workOrders?.items]
  );
  const totalWorkOrders = workOrders.length;
  const urgentWorkOrders = workOrders.filter((wo) => wo.isUrgent).length;

  // Don't render if user is housekeeper or inspector
  if (isHousekeeper || isInspector) {
    return null;
  }

  return (
    <>
      {/* Total Work Orders Card */}
      <Link 
        to="/dashboard/work-orders" 
        className="block bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow"
      >
        <div className="flex items-center justify-between">
          <div className="bg-orange-100 p-3 rounded-lg">
            <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <span className="text-2xl font-bold">
            {workOrdersLoading ? '...' : workOrdersError ? 'Error' : totalWorkOrders}
          </span>
        </div>
        <div className="mt-2 text-gray-600">
          {shouldFilterByRole ? 'My Work Orders' : 'Work Orders'}
        </div>
        <div className="mt-1 text-xs text-gray-500">
          {assignedAreaLabel}
        </div>
        <div className="mt-1 text-xs font-medium text-orange-700">
          Unassigned Work Orders
        </div>
        {workOrdersError && (
          <div className="mt-2 text-sm text-red-600">
            {workOrdersError.message || 'Failed to load work orders'}
          </div>
        )}
      </Link>

      {/* Urgent Work Orders Card - hidden for call center */}
      {!isCallCenter && (
        <Link 
          to="/dashboard/work-orders" 
          className="block bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div className="bg-red-100 p-3 rounded-lg">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <span className="text-2xl font-bold">
              {workOrdersLoading ? '...' : workOrdersError ? 'Error' : urgentWorkOrders}
            </span>
          </div>
          <div className="mt-2 text-gray-600">
            {shouldFilterByRole ? 'My Urgent Work Orders' : 'Urgent Work Orders'}
          </div>
          {workOrdersError && (
            <div className="mt-2 text-sm text-red-600">
              {workOrdersError.message || 'Failed to load urgent work orders'}
            </div>
          )}
        </Link>
      )}
    </>
  );
};

export default WorkOrderWidget;
